/** @jsx jsx */
import { jsx } from '@emotion/react'
import { useRef } from 'react'
import { css } from '@emotion/react/macro'
import CheckBox from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { AnimatePresence, motion } from 'framer-motion'

import {
  baseColor,
  disabledIconColor,
  gray,
  hoverColor,
  primaryTextColor,
  secondaryTextColor
} from '../../../../lib/styles/colors'
import { tertiaryBodyCss } from '../../../../lib/styles/typography'
import { rippleContainerCss } from '../../../../lib/styles/cssStyles'
import Ripple from '../../buttons/Ripple'
import useRipple from '../../../../lib/hooks/useRipple'

export type CheckboxProps = {
  /** Aria label to add to the checkbox */
  ariaLabel?: string
  /** Checked value for the checkbox */
  checked: boolean
  /** Optional class name to apply to the checkbox */
  className?: string
  /** Description to be rendered below the label */
  description?: string
  /** Whether the checkbox is disabled */
  disabled?: boolean
  /** ID to use for the checkbox */
  id: string
  /** Text to display in the info tooltip */
  infoText?: string
  /** Label for the checkbox */
  label?: string
  /** name of value */
  name?: string
  /** Function to call when the checkbox is clicked */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Prevent actions from bubbling up the DOM. */
  stopPropagation?: boolean
}

const defaultProps: Omit<CheckboxProps, 'checked' | 'id' | 'onChange'> = {
  ariaLabel: '',
  className: '',
  description: '',
  disabled: false,
  infoText: '',
  label: '',
  name: '',
  stopPropagation: false
}

const checkboxWrapperCss = css`
  display: flex;
  outline: none;
  align-items: center;
  padding: 6px;
  margin: 0px;

  :hover {
    cursor: pointer;
  }

  p {
    margin: 0;
    :hover {
      cursor: pointer;
    }
  }
`

const checkboxLabelCss = css`
  display: flex;
  min-height: 20px;
`

const infoContainerCss = css`
  display: flex;
  align-self: center;
  margin-left: 12px;
`

const tooltipTextCss = css`
  padding: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.75px;
`

// unique font
const labelCss = css`
  font-size: 16px;
  line-height: 18px;
  letter-spacing: 0.4px;
  color: ${primaryTextColor};
`

const checkboxInputCss = css`
  opacity: 0;
  position: absolute;
  :hover {
    cursor: pointer;
  }
`

const descriptionCss = css`
  ${tertiaryBodyCss}
  color: ${secondaryTextColor};
  padding-top: 6px;
`

const disabledCss = css`
  cursor: not-allowed;

  :hover {
    cursor: not-allowed;
  }

  p {
    :hover {
      cursor: not-allowed;
    }
  }
`

const Checkbox = ({
  ariaLabel,
  checked,
  className,
  description,
  disabled,
  id,
  infoText,
  label,
  name,
  onChange,
  stopPropagation
}: CheckboxProps) => {
  const container = useRef<HTMLSpanElement>(null)
  const [ripples, focusRipple, { onTapStart, onTapEnd, onFocus, onBlur }] = useRipple(container)

  const getCheckboxColor = () => {
    if (disabled) {
      return disabledIconColor
    }
    return checked ? baseColor : primaryTextColor
  }

  const checkboxCss = css`
    border-radius: 50%;
    display: flex;
    height: 24px;
    width: 24px;
    color: ${getCheckboxColor()};
    padding: 12px;
    cursor: pointer;
    margin-top: ${description ? '-16px' : 0};
    transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    :hover,
    :focus-within {
      background-color: ${hoverColor};
      transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }
  `

  const labelWrapperCss = css`
    display: ${description ? 'block' : 'flex'};
    align-self: center;
  `

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!disabled) {
      const event = {
        ...e,
        stopPropagation: e.stopPropagation,
        target: { ...e.target, checked: !checked, name }
      }
      onChange(event as any)
    }
  }

  return (
    <div css={[checkboxWrapperCss, disabled && disabledCss]} className={className}>
      <label htmlFor={id} css={checkboxLabelCss}>
        <motion.span
          css={[checkboxCss, disabled && disabledCss]}
          onBlur={onBlur}
          onClick={stopPropagation ? handleInputClick : () => {}}
          onFocus={onFocus}
          onTap={onTapEnd}
          onTapStart={(e: MouseEvent | TouchEvent | PointerEvent) => {
            onTapStart(e)
          }}
        >
          <input
            id={id}
            aria-label={ariaLabel}
            checked={checked}
            css={[checkboxInputCss, disabled && disabledCss]}
            disabled={disabled}
            name={name}
            onChange={onChange}
            onClick={stopPropagation ? handleInputClick : () => {}}
            type="checkbox"
          />
          <span css={rippleContainerCss} ref={container}>
            <AnimatePresence>
              {ripples?.map((r) => (
                <Ripple key={r.key} pulsate={r.pulsate} size={r.size} x={r.x} y={r.y} />
              ))}
              {focusRipple && (
                <Ripple
                  key={focusRipple.key}
                  pulsate={focusRipple.pulsate}
                  size={focusRipple.size}
                  x={focusRipple.x}
                  y={focusRipple.y}
                />
              )}
            </AnimatePresence>
          </span>
          {checked ? <CheckBox /> : <CheckBoxOutlineBlankIcon />}
        </motion.span>
        {(label || description) && (
          <div css={labelWrapperCss}>
            <p css={labelCss}>{label}</p>
            {description && <p css={descriptionCss}>{description}</p>}
          </div>
        )}
        {infoText && (
          <div css={infoContainerCss}>
            <Tooltip
              aria-hidden={false}
              aria-label={infoText}
              tabIndex={0}
              title={<div css={tooltipTextCss}>{infoText}</div>}
            >
              <InfoIcon className="info-icon" fontSize="small" htmlColor={gray} />
            </Tooltip>
          </div>
        )}
      </label>
    </div>
  )
}

Checkbox.defaultProps = defaultProps

export default Checkbox
